import Vue from 'vue';

interface ShippingDialogState {
  isOpen: boolean;
  orderId: number | null;
  pickTicketId: number | null;
  isLoading: boolean;
  error: string | null;
  lastScanData: {
    pickTicketId: number;
    timestamp: number;
    processed: boolean;
  } | null;
  labelHistory: Array<{
    pickTicketId: number;
    trackingNumber: string;
    createdAt: Date;
    orderId: number;
  }>;
}

const state: ShippingDialogState = {
  isOpen: false,
  orderId: null,
  pickTicketId: null,
  isLoading: false,
  error: null,
  lastScanData: null,
  labelHistory: []
};

const mutations = {
  OPEN_DIALOG(state: ShippingDialogState, payload: { orderId: number, pickTicketId: number }) {
    state.isOpen = true;
    state.orderId = payload.orderId;
    state.pickTicketId = payload.pickTicketId;
    state.error = null;
  },
  
  CLOSE_DIALOG(state: ShippingDialogState) {
    state.isOpen = false;
    state.orderId = null;
    state.pickTicketId = null;
    state.error = null;
  },
  
  SET_LOADING(state: ShippingDialogState, loading: boolean) {
    state.isLoading = loading;
  },
  
  SET_ERROR(state: ShippingDialogState, error: string | null) {
    state.error = error;
  },
  
  SET_LAST_SCAN(state: ShippingDialogState, scanData: any) {
    state.lastScanData = scanData;
  },
  
  ADD_TO_HISTORY(state: ShippingDialogState, labelData: any) {
    state.labelHistory.unshift({
      pickTicketId: labelData.pickTicketId,
      trackingNumber: labelData.trackingNumber,
      createdAt: new Date(),
      orderId: labelData.orderId
    });
    // Keep only last 10 labels in history
    if (state.labelHistory.length > 10) {
      state.labelHistory.pop();
    }
  }
};

const actions = {
  async openShippingDialog({ commit, dispatch, state }: any, pickTicketId: number) {
    // Guard: reset stale loading state from a previous failed attempt
    if (state.isLoading) {
      console.warn('[shippingDialog] Resetting stale loading state before new dialog open');
      commit('SET_LOADING', false);
    }

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      // Find order by pick ticket
      const orderData = await dispatch('findOrderByPickTicket', pickTicketId, { root: true });

      // Check user permissions
      const hasPermission = await dispatch('checkLabelPermission', null, { root: true });
      if (!hasPermission) {
        throw new Error('You do not have permission to create shipping labels');
      }

      // Open dialog
      commit('OPEN_DIALOG', {
        orderId: orderData.salesOrderId,
        pickTicketId: pickTicketId
      });

      // Mark scan as processed
      commit('SET_LAST_SCAN', {
        pickTicketId,
        timestamp: Date.now(),
        processed: true
      });

    } catch (error: any) {
      // Fully reset dialog state on error so subsequent scans aren't blocked
      commit('CLOSE_DIALOG');
      commit('SET_ERROR', error.message);
      // Show error notification
      Vue.prototype.$toast?.error(error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  closeShippingDialog({ commit }: any) {
    commit('CLOSE_DIALOG');
  },
  
  addLabelToHistory({ commit }: any, labelData: any) {
    commit('ADD_TO_HISTORY', labelData);
  }
};

const getters = {
  isDialogOpen: (state: ShippingDialogState) => state.isOpen,
  currentOrderId: (state: ShippingDialogState) => state.orderId,
  currentPickTicketId: (state: ShippingDialogState) => state.pickTicketId,
  isLoading: (state: ShippingDialogState) => state.isLoading,
  dialogError: (state: ShippingDialogState) => state.error,
  lastScan: (state: ShippingDialogState) => state.lastScanData,
  recentLabels: (state: ShippingDialogState) => state.labelHistory
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};